import { Box, Stack, Text, Title } from "@mantine/core"
import { useElementSize } from "@mantine/hooks"
import { AnimatePresence, motion } from "motion/react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { AppTextarea } from "@/components/forms"
import { QuestionBoolean } from "./QuestionBoolean"
import { QuestionDate } from "./QuestionDate"
import { QuestionLikert } from "./QuestionLikert"
import { QuestionMultiple } from "./QuestionMultiple"
import { QuestionNote } from "./QuestionNote"
import { QuestionNumber } from "./QuestionNumber"
import { QuestionPersonal } from "./QuestionPersonal"
import { QuestionScale } from "./QuestionScale"
import { QuestionSelect } from "./QuestionSelect"
import { QuestionString } from "./QuestionString"
import { QuestionTags } from "./QuestionTags"
import type { TQuestionProps } from "./types"

export function QuestionAnimated({ dir, idx, ...props }: TQuestionProps & { dir?: "next" | "prev"; idx: any }) {
  const enterX = dir === "next" ? 200 : -200
  const exitX = dir === "next" ? -200 : 200

  const [minWidth, setMinWidth] = useState(0)
  const { ref, width } = useElementSize()

  useEffect(() => {
    if (width > minWidth) setMinWidth(width)
  }, [width, minWidth])

  return (
    <Box ref={ref} style={{ overflow: "hidden", width: "100%", minWidth }}>
      <AnimatePresence mode="wait">
        <motion.div
          key={`${idx}-${dir}`}
          animate={{ x: 0, opacity: 1 }}
          initial={{ x: enterX, opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          exit={{ x: exitX, opacity: 0, filter: "blur(2px)" }}
        >
          <Question {...props} />
        </motion.div>
      </AnimatePresence>
    </Box>
  )
}

export function Question(props: TQuestionProps) {
  const { t } = useTranslation()

  const { question } = props
  const stringQuestion = question.type == "string" || (question.type == "personal" && question.is_commentable)
  const { type } = question

  return (
    <Stack>
      <Title fz={30} lh="34px" mb="sm" fw={500}>
        {question.title}
        {question.is_required && (
          <Text span c="red" fz="inherit" ml={5} fw={500}>
            *
          </Text>
        )}
      </Title>

      {type == "select" ? (
        <QuestionSelect {...props} />
      ) : type == "multiple" ? (
        <QuestionMultiple {...props} />
      ) : type == "scale" ? (
        <QuestionScale {...props} />
      ) : type == "likert" ? (
        <QuestionLikert {...props} />
      ) : type == "date" ? (
        <QuestionDate {...props} />
      ) : type == "personal" ? (
        <QuestionPersonal {...props} />
      ) : type == "tags" ? (
        <QuestionTags {...props} />
      ) : type == "number" ? (
        <QuestionNumber {...props} />
      ) : type == "boolean" ? (
        <QuestionBoolean {...props} />
      ) : (
        <QuestionString {...props} />
      )}
      <QuestionNote question={question} />

      {question.is_commentable && !stringQuestion && (
        <AppTextarea
          size="lg"
          value={props.comment ?? ""}
          placeholder={t("Add your comment")}
          onChange={(value: string) => props.setComment(value)}
        />
      )}
    </Stack>
  )
}
